import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CustomInput from "../../component/common/inputField";
import CustomModal from "../../component/common/customModal";
import CustomSelect from "../../component/common/customSelect";
import returnRequestService from "../../../services/ReturnRequestService";
import { showToast } from "../../component/common/toastNotification";
import saleOrderServices, {
  fetchAllCatalogProducts,
  parseOrderItems,
} from "../../../services/SalesOrderService";
import { useTranslation } from "react-i18next";
import { localizeReturnReason } from "../../../helper/localizeReturnReason";
import CircularProgress from "@mui/material/CircularProgress";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { InitiatedWorkflowData } from "../../../types/returnManagement";
import { NJIM } from "../../../theme/njimTheme";

interface Props {
  open: boolean;
  onClose: () => void;
}
interface OrderItem {
  productId: string | number;
  name: string;
  code: string;
  category?: string;
}
interface BatchHistory {
  lotNumber: string;
  expirationDate: string;
  receivedDate: string;
}
interface BatchBalance {
  lotNumber: string;
  customerHeldQty: number;
  expirationDate?: string;
}
// Raw record from GET /api/returns/GetCustomerBatchBalance
interface ReturnableRecord {
  itemNo: string;
  itemName?: string;
  lotNumber: string;
  customerHeldQty: number;
  expirationDate?: string;
  stockSite: string;
}
interface CartItem {
  id: string;
  itemId: string | number;
  name: string;
  batchNumber: string;
  quantity: number;
  expiryDate: string;
  reason: string;
  unitPrice: number;
}

const InitiateModal: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();

  // All batch-balance records — used to look up batches in-memory
  const allReturnableItems = useRef<ReturnableRecord[]>([]);
  // SKU code → MongoDB product id (same as mobile app POST /returns payload)
  const codeToMongoId = useRef<Map<string, string>>(new Map());
  const submittingRef = useRef(false);

  const [orderList, setOrderList] = useState<OrderItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [productsLoading, setProductsLoading] = useState(false);
  const [batchHistory, setBatchHistory] = useState<BatchHistory[]>([]);
  const [batchBalances, setBatchBalances] = useState<BatchBalance[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);

  // Tracks quantities already submitted this session (key: "itemNo|batchNumber")
  const submittedQty = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (open) {
      fetchReturnableItems();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const buildOrderList = (
    records: ReturnableRecord[],
    nameMap: Map<string, string>,
    categoryMap: Map<string, string> = new Map()
  ): OrderItem[] => {
    const seen = new Map<string, OrderItem>();
    records.forEach((r) => {
      if (!r.itemNo || r.customerHeldQty <= 0 || seen.has(r.itemNo)) return;
      const codeKey = r.itemNo.toUpperCase();
      const name =
        (r.itemName ?? "").trim() ||
        nameMap.get(codeKey) ||
        r.itemNo;
      seen.set(r.itemNo, {
        productId: r.itemNo,
        name,
        code: r.itemNo,
        category: categoryMap.get(codeKey) ?? "",
      });
    });
    return Array.from(seen.values());
  };

  const applyProductRecordsToMaps = (
    records: Record<string, unknown>[],
    nameMap: Map<string, string>,
    idMap: Map<string, string>,
    categoryMap: Map<string, string>
  ) => {
    records.forEach((p) => {
      const code = String(p.code ?? p.Code ?? "").trim().toUpperCase();
      const id = String(p.id ?? p.Id ?? p.productId ?? p.ProductId ?? "").trim();
      const name = String(
        p.name ?? p.Name ?? p.title ?? p.Title ?? ""
      ).trim();
      const category = String(p.category ?? p.Category ?? "").trim();
      if (code && name) nameMap.set(code, name);
      if (code && id) idMap.set(code, id);
      if (code && category) categoryMap.set(code, category);
    });
  };

  const applyOrderItemsToMaps = (
    orderItems: Record<string, unknown>[],
    nameMap: Map<string, string>,
    idMap: Map<string, string>,
    categoryMap: Map<string, string>
  ) => {
    orderItems.forEach((item) => {
      const code = String(item.code ?? item.Code ?? "").trim().toUpperCase();
      const id = String(item.productId ?? item.ProductId ?? "").trim();
      const name = String(item.name ?? item.Name ?? "").trim();
      const category = String(item.category ?? item.Category ?? "").trim();
      if (code && name) nameMap.set(code, name);
      if (code && id) idMap.set(code, id);
      if (code && category) categoryMap.set(code, category);
    });
  };

  // Batch balance first (includes itemName from Sage); catalog/orders enrich Mongo id mapping.
  const fetchReturnableItems = async () => {
    setProductsLoading(true);
    try {
      const itemsRes = await returnRequestService.getReturnableItems();
      const raw: Record<string, unknown>[] = Array.isArray(itemsRes?.data)
        ? (itemsRes.data as Record<string, unknown>[])
        : [];

      const records: ReturnableRecord[] = raw.map((r) => ({
        itemNo: String(r.itemNo ?? r.ItemNo ?? "").trim(),
        itemName: String(r.itemName ?? r.ItemName ?? "").trim(),
        lotNumber: String(r.lotNumber ?? r.LotNumber ?? "").trim(),
        customerHeldQty: Number(r.customerHeldQty ?? r.CustomerHeldQty ?? 0),
        expirationDate:
          r.expirationDate || r.ExpirationDate
            ? String(r.expirationDate ?? r.ExpirationDate)
            : undefined,
        stockSite: String(r.stockSite ?? r.StockSite ?? "").trim(),
      }));

      allReturnableItems.current = records;
      setOrderList(buildOrderList(records, new Map()));
      setSelectedCategory("");
      setProductsLoading(false);

      try {
        const [catalogProducts, orderItemsRes] = await Promise.all([
          fetchAllCatalogProducts(),
          saleOrderServices.getOrderItemList(),
        ]);

        const nameMap = new Map<string, string>();
        const idMap = new Map<string, string>();
        const categoryMap = new Map<string, string>();
        applyProductRecordsToMaps(catalogProducts, nameMap, idMap, categoryMap);
        applyOrderItemsToMaps(
          parseOrderItems(orderItemsRes?.data),
          nameMap,
          idMap,
          categoryMap
        );

        codeToMongoId.current = idMap;
        setOrderList(buildOrderList(records, nameMap, categoryMap));
      } catch (catalogError) {
        console.error("Error resolving return product names:", catalogError);
        codeToMongoId.current = new Map();
      }
    } catch (error) {
      console.error("Error fetching returnable items:", error);
      setOrderList([]);
      setProductsLoading(false);
    }
  };

  const [form, setForm] = useState({
    item: "",
    quantity: "",
    batch: "",
    reason: "",
    notes: "",
  });
  const [errors, setErrors] = useState({
    item: "",
    quantity: "",
    batch: "",
    reason: "",
  });

  const uncategorizedLabel = t("returnsManagement.uncategorized", {
    defaultValue: "Uncategorized",
  });

  const availableCategories = useMemo(() => {
    const categories = new Set<string>();
    let hasUncategorized = false;
    orderList.forEach((item) => {
      if (item.category?.trim()) {
        categories.add(item.category.trim());
      } else {
        hasUncategorized = true;
      }
    });
    const sorted = Array.from(categories).sort((a, b) => a.localeCompare(b));
    if (hasUncategorized) sorted.push(uncategorizedLabel);
    return sorted;
  }, [orderList, uncategorizedLabel]);

  const filteredOrderList = useMemo(() => {
    if (!selectedCategory) return [];
    return orderList.filter((item) => {
      const itemCategory = item.category?.trim() || uncategorizedLabel;
      return itemCategory === selectedCategory;
    });
  }, [orderList, selectedCategory, uncategorizedLabel]);

  const resetProductSelection = () => {
    setSelectedBatch(null);
    setBatchHistory([]);
    setBatchBalances([]);
    setForm((prev) => ({
      ...prev,
      item: "",
      quantity: "",
      batch: "",
      reason: "",
    }));
    setErrors((prev) => ({
      ...prev,
      item: "",
      quantity: "",
      batch: "",
      reason: "",
    }));
  };

  const handleCategoryChange = (
    valueOrEvent: string | number | SelectChangeEvent<string | number>
  ) => {
    const value =
      typeof valueOrEvent === "object"
        ? valueOrEvent.target.value
        : valueOrEvent;
    setSelectedCategory(String(value));
    resetProductSelection();
  };

  const getMaxReturnQty = (_itemId: string, batchNumber: string): { max: number; batchSpecific: boolean } => {
    const normalize = (s?: string) => (s ?? "").trim().toLowerCase();
    const batchBalance = batchBalances.find(
      (b) => normalize(b.lotNumber) === normalize(batchNumber)
    );
    if (batchBalance !== undefined) {
      return { max: batchBalance.customerHeldQty, batchSpecific: true };
    }
    return { max: Infinity, batchSpecific: false };
  };

  const validateFields = () => {
    let isValid = true;
    const newErrors = { item: "", quantity: "", batch: "", reason: "" };

    if (!form.item) {
      newErrors.item = t("returnsManagement.itemRequired");
      isValid = false;
    }
    if (!form.quantity || Number(form.quantity) <= 0) {
      newErrors.quantity = t("returnsManagement.validQuantityRequired");
      isValid = false;
    } else if (form.item && form.batch) {
      const { max } = getMaxReturnQty(form.item, form.batch);
      if (max !== Infinity && Number(form.quantity) > max) {
        newErrors.quantity = t("returnsManagement.quantityExceedsOrder", {
          max,
          defaultValue: `Quantity cannot exceed ordered amount (${max})`,
        });
        isValid = false;
      }
    }
    if (!form.batch) {
      newErrors.batch = t("returnsManagement.batchNumberRequired");
      isValid = false;
    }
    if (!form.reason) {
      newErrors.reason = t("returnsManagement.reasonRequired");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, quantity: val }));
    if (form.item && form.batch && val && Number(val) > 0) {
      const { max } = getMaxReturnQty(form.item, form.batch);
      if (max !== Infinity && Number(val) > max) {
        setErrors((prev) => ({
          ...prev,
          quantity: t("returnsManagement.quantityExceedsOrder", {
            max,
            defaultValue: `Quantity cannot exceed ordered amount (${max})`,
          }),
        }));
        return;
      }
    }
    setErrors((prev) => ({ ...prev, quantity: "" }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleSelectChange = (
    name: string,
    valueOrEvent: string | number | SelectChangeEvent<string | number>
  ) => {
    const value =
      typeof valueOrEvent === "object"
        ? valueOrEvent.target.value
        : valueOrEvent;

    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "item" && value) {
      setSelectedBatch(null);
      setBatchHistory([]);
      setBatchBalances([]);
      setForm((prev) => ({ ...prev, batch: "" }));
      // itemNo IS the product code — load batches from in-memory data (no API call)
      loadBatchesForProduct(value.toString());
    }
  };

  // In-memory batch loader — no API call needed since all data was loaded upfront.
  const loadBatchesForProduct = (itemNo: string) => {
    setBatchLoading(true);
    try {
      const original = allReturnableItems.current.filter(
        (r) => r.itemNo.toLowerCase() === itemNo.toLowerCase() && r.customerHeldQty > 0
      );

      // Deduct quantities already submitted this session and currently in cart
      const balances: BatchBalance[] = original.map((b) => {
        const key = `${b.itemNo}|${b.lotNumber.toLowerCase()}`;
        const alreadySubmitted = submittedQty.current.get(key) ?? 0;
        const inCart = cart
          .filter(
            (ci) =>
              ci.itemId.toString() === itemNo &&
              ci.batchNumber.trim().toLowerCase() === b.lotNumber.trim().toLowerCase()
          )
          .reduce((sum, ci) => sum + ci.quantity, 0);
        return {
          lotNumber: b.lotNumber,
          expirationDate: b.expirationDate,
          customerHeldQty: Math.max(0, b.customerHeldQty - alreadySubmitted - inCart),
        };
      });
      setBatchBalances(balances);

      // Batch table rows — show all held batches (unique)
      const seen = new Set<string>();
      const displayed: BatchHistory[] = [];
      original.forEach((b) => {
        const lot = b.lotNumber.trim().toLowerCase();
        if (seen.has(lot)) return;
        seen.add(lot);
        displayed.push({
          lotNumber: b.lotNumber,
          expirationDate: b.expirationDate ?? "",
          receivedDate: "",
        });
      });
      setBatchHistory(displayed);
    } finally {
      setBatchLoading(false);
    }
  };

  const handleClose = () => {
    setForm({ item: "", quantity: "", batch: "", reason: "", notes: "" });
    setErrors({ item: "", quantity: "", batch: "", reason: "" });
    setSelectedCategory("");
    setCart([]);
    setSelectedBatch(null);
    setBatchBalances([]);
    setBatchHistory([]);
    onClose();
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    if (cart.length === 0) {
      showToast(t("returnsManagement.addAtLeastOneItem"), "error");
      return;
    }

    const totalAmount = cart.reduce(
      (sum, item) => sum + item.quantity * item.unitPrice,
      0
    );

    const items: InitiatedWorkflowData["items"] = [];
    for (const item of cart) {
      const product = orderList.find(
        (o) =>
          o.code === String(item.itemId) ||
          o.productId.toString() === String(item.itemId)
      );
      const productCode = (product?.code ?? String(item.itemId)).trim();
      const mongoId =
        codeToMongoId.current.get(productCode.toUpperCase()) ?? "";

      if (!mongoId) {
        showToast(t("returnsManagement.productNotFound"), "error");
        return;
      }

      items.push({
        itemId: mongoId,
        quantity: item.quantity,
        batchNumber: item.batchNumber,
        reason: item.reason,
      });
    }

    const payload: InitiatedWorkflowData = {
      amount: totalAmount,
      items,
      additionalNotes: form.notes || "",
    };

    submittingRef.current = true;
    try {
      await returnRequestService.initiateReturn(payload);

      // Record submitted quantities so reopening the modal shows correct remaining qty
      cart.forEach((item) => {
        const product = orderList.find(
          (o) =>
            o.code === String(item.itemId) ||
            o.productId.toString() === String(item.itemId)
        );
        const productCode = product?.code ?? String(item.itemId);
        const key = `${productCode}|${item.batchNumber.toLowerCase()}`;
        submittedQty.current.set(key, (submittedQty.current.get(key) ?? 0) + item.quantity);
      });

      showToast(t("returnsManagement.returnSubmittedSuccessfully"), "success");
      handleClose();
    } catch (error) {
      console.error("Failed to submit return:", error);
      showToast(t("returnsManagement.failedToSubmitReturn"), "error");
    } finally {
      submittingRef.current = false;
    }
  };

  const handleAddItem = () => {
    if (!validateFields()) return;

    const selectedProduct = orderList.find(
      (item) => item.productId.toString() === form.item.toString()
    );
    if (!selectedProduct) {
      showToast(t("returnsManagement.productNotFound"), "error");
      return;
    }

    const selectedBatchInHistory = batchHistory.some(
      (batch) => batch.lotNumber?.trim() === form.batch?.trim()
    );
    if (!selectedBatchInHistory) {
      showToast(t("returnsManagement.batchNumberRequired"), "error");
      return;
    }

    let expiryDate = "";
    if (selectedBatch) {
      const batch = batchHistory.find((b) => b.lotNumber === selectedBatch);
      if (batch) {
        expiryDate = batch.expirationDate;
      }
    }

    const unitPrice = 0;

    const newCartItem: CartItem = {
      id: `${form.item}-${form.batch}-${Date.now()}`,
      itemId: form.item,
      name: selectedProduct.name,
      batchNumber: form.batch,
      quantity: Number(form.quantity),
      expiryDate: expiryDate,
      reason: form.reason,
      unitPrice,
    };

    setCart((prev) => [...prev, newCartItem]);

    // Deduct the added quantity from in-memory batch balances so the same batch
    // cannot be over-requested if the user adds another return item for it.
    setBatchBalances((prev) =>
      prev.map((b) =>
        b.lotNumber.trim().toLowerCase() === form.batch.trim().toLowerCase()
          ? { ...b, customerHeldQty: Math.max(0, b.customerHeldQty - Number(form.quantity)) }
          : b
      )
    );

    setForm((prev) => ({
      ...prev,
      item: "",
      quantity: "",
      batch: "",
      reason: "",
    }));
    setSelectedBatch(null);
    setErrors({
      item: "",
      quantity: "",
      batch: "",
      reason: "",
    });

    showToast(t("returnsManagement.itemAddedToCart"), "success");
  };

  const handleRemoveItem = (id: string) => {
    const removed = cart.find((item) => item.id === id);
    setCart((prev) => prev.filter((item) => item.id !== id));
    // Restore the qty back to the balance so the batch shows the correct available qty again
    if (removed) {
      setBatchBalances((prev) =>
        prev.map((b) =>
          b.lotNumber.trim().toLowerCase() === removed.batchNumber.trim().toLowerCase()
            ? { ...b, customerHeldQty: b.customerHeldQty + removed.quantity }
            : b
        )
      );
    }
    showToast(t("returnsManagement.itemRemovedFromCart"), "success");
  };
  const RETURN_REASON_OPTIONS = [
    { key: "returnsManagement.damaged" },
    { key: "returnsManagement.expired" },
    { key: "returnsManagement.wrongItem" },
    { key: "returnsManagement.defective" },
    { key: "returnsManagement.other" },
  ];
  const localizedReasonOptions = RETURN_REASON_OPTIONS.map((opt) => {
    const translated = t(opt.key);
    return {
      label: translated,
      value: translated,
    };
  });
  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      ModalTitle={t("returnsManagement.initiateProductReturn")}
      ModalDescription={t("returnsManagement.completeFormToSubmit")}
      width="35%"
      bgcolor={NJIM.muted}
    >
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleAddItem();
        }}
      >
      <Card sx={{ padding: 2, mt: 1.5, borderRadius: "12px !important" }}>
        <Typography sx={{ fontWeight: 600, fontSize: "15px", color: NJIM.textPrimary }}>
          {t("returnsManagement.Add Product to Return")}
        </Typography>

        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12} sm={12}>
            <CustomSelect
              label={t("salesOrder.category", { defaultValue: "Category" })}
              name="category"
              placeHolder={
                productsLoading
                  ? t("common.loading")
                  : t("returnsManagement.selectCategory", { defaultValue: "Select category" })
              }
              value={selectedCategory}
              onChange={(value) => handleCategoryChange(value)}
              options={availableCategories.map((category) => ({
                label: category,
                value: category,
              }))}
              resettable
              searchable
              disabled={productsLoading || availableCategories.length === 0}
              required
            />
            {!productsLoading && availableCategories.length === 0 && orderList.length === 0 && (
              <Typography sx={{ color: NJIM.textMuted, fontSize: "12px", mt: 0.5 }}>
                {t("returnsManagement.noPurchasedItems")}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={12}>
            <CustomSelect
              label={t("returnsManagement.product")}
              name="item"
              placeHolder={
                !selectedCategory
                  ? t("returnsManagement.selectCategoryFirst", {
                      defaultValue: "Select a category first",
                    })
                  : productsLoading
                  ? t("common.loading")
                  : t("returnsManagement.selectProduct")
              }
              value={form.item}
              onChange={(value) => handleSelectChange("item", value)}
              options={filteredOrderList.map((item) => ({
                label: item.name,
                value: item.productId,
              }))}
              resettable
              searchable
              disabled={
                productsLoading ||
                !selectedCategory ||
                filteredOrderList.length === 0
              }
              required
              error={errors.item}
            />
            {productsLoading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <CircularProgress size={12} sx={{ color: NJIM.mint }} />
                <Typography sx={{ color: NJIM.textSecondary, fontSize: "12px" }}>
                  {t("returnsManagement.loadingProducts", { defaultValue: "Loading your products…" })}
                </Typography>
              </Box>
            )}
            {!productsLoading && selectedCategory && filteredOrderList.length === 0 && (
              <Typography sx={{ color: NJIM.textMuted, fontSize: "12px", mt: 0.5 }}>
                {t("returnsManagement.noProductsInCategory", {
                  defaultValue: "No returnable products in this category.",
                })}
              </Typography>
            )}
          </Grid>
          {form.item && (
            <>
              <Grid item xs={12} sm={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1 }}>
                  <InfoOutlinedIcon
                    sx={{ fontSize: 16, color: NJIM.mint }}
                  />
                  <Typography sx={{ fontWeight: 600, fontSize: "14px", color: NJIM.textSecondary }}>
                    {t("returnsManagement.batchHistory")}
                  </Typography>
                </Box>
                <Table
                  sx={{
                    border: "1px solid rgba(226,232,240,0.5)",
                    borderRadius: "10px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": {
                      padding: "10px 12px",
                      borderBottom: "1px solid rgba(226,232,240,0.4)",
                      fontSize: "13px",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ background: "rgba(248,249,250,0.5)" }}>
                      <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        {t("returnsManagement.batchNumber")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        {t("returnsManagement.expiryDate")}
                      </TableCell>
                      {batchBalances.length > 0 && (
                        <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                          {t("returnsManagement.availableQty", { defaultValue: "Available Qty" })}
                        </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        {t("common.actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batchLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ color: NJIM.textMuted }}>
                          {t("common.loading")}
                        </TableCell>
                      </TableRow>
                    ) : batchHistory.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{ textAlign: "center", color: NJIM.textMuted, padding: 4 }}
                        >
                          {t("common.noData")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      batchHistory.map((batch, index) => (
                        <TableRow
                          key={index}
                          sx={{ transition: "background 0.2s ease", "&:hover": { background: "rgba(248,249,250,0.5)" } }}
                        >
                          <TableCell sx={{ fontWeight: 500, color: NJIM.textPrimary }}>{batch.lotNumber}</TableCell>
                          <TableCell sx={{ color: NJIM.textSecondary }}>
                            {batch.expirationDate
                              ? new Date(batch.expirationDate)
                                  .toISOString()
                                  .split("T")[0]
                              : "-"}
                          </TableCell>
                          {batchBalances.length > 0 && (() => {
                            const bal = batchBalances.find(
                              (b) => b.lotNumber.trim().toLowerCase() === batch.lotNumber.trim().toLowerCase()
                            );
                            return (
                              <TableCell sx={{ color: NJIM.ink, fontWeight: 600 }}>
                                {bal !== undefined ? bal.customerHeldQty : "-"}
                              </TableCell>
                            );
                          })()}
                          <TableCell sx={{ width: "90px" }}>
                            <Button
                              type="button"
                              variant={selectedBatch === batch.lotNumber ? "contained" : "outlined"}
                              onClick={() => {
                                setSelectedBatch(batch.lotNumber);
                                setForm((prev) => ({
                                  ...prev,
                                  batch: batch.lotNumber,
                                }));
                              }}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "12px",
                                borderRadius: "8px",
                                ...(selectedBatch === batch.lotNumber
                                  ? {
                                      background: "rgba(5,150,105,0.1)",
                                      color: NJIM.success,
                                      border: "1px solid rgba(5,150,105,0.2)",
                                      boxShadow: "none",
                                      "&:hover": { background: "rgba(5,150,105,0.15)", boxShadow: "none" },
                                    }
                                  : {
                                      borderColor: "rgba(93,183,167,0.4)",
                                      color: NJIM.ink,
                                      "&:hover": { borderColor: NJIM.mint, background: "rgba(93,183,167,0.08)" },
                                    }),
                              }}
                            >
                              {selectedBatch === batch.lotNumber
                                ? t("common.selected")
                                : t("common.select")}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Grid>
            </>
          )}
          {/* Batch Number */}
          <Grid item xs={12} sm={6}>
            <CustomInput
              name="batch"
              label={t("returnsManagement.batchNumber")}
              value={form.batch}
              onChange={handleChange}
              placeholder={t("returnsManagement.batchNumberPlaceholder")}
              required
              error={errors.batch}
              readOnly
              backgroundColor={NJIM.muted}
            />
          </Grid>
          {/* Quantity */}
          <Grid item xs={12} sm={6}>
            <CustomInput
              name="quantity"
              label={t("common.quantity")}
              value={form.quantity}
              onChange={handleQuantityChange}
              placeholder={t("returnsManagement.enterQuantity")}
              type="number"
              required
              error={errors.quantity}
            />
            {form.item && form.batch && (() => {
              const { max, batchSpecific } = getMaxReturnQty(form.item, form.batch);
              if (max === Infinity) return null;
              const exceeded = Number(form.quantity) > max;
              return (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: exceeded ? NJIM.danger : NJIM.textSecondary,
                    fontWeight: exceeded ? 600 : 400,
                    mt: 0.5,
                    ml: 0.5,
                  }}
                >
                  {batchSpecific
                    ? t("returnsManagement.maxReturnQty", { max, defaultValue: `Max returnable for this batch: ${max}` })
                    : t("returnsManagement.maxReturnQtyTotal", { max, defaultValue: `Max returnable (total ordered): ${max}` })}
                </Typography>
              );
            })()}
          </Grid>

          {/* Return Reason */}

          <Grid item xs={12} sm={12}>
            <CustomSelect
              name="reason"
              label={t("returnsManagement.returnReason")}
              placeHolder={t("returnsManagement.returnReason")}
              value={form.reason}
              onChange={(value) => handleSelectChange("reason", value)}
              options={localizedReasonOptions}
              required
              error={errors.reason}
            />
          </Grid>
          <Grid item xs={12} sm={12} display="flex" justifyContent="end" p={0}>
            <Button
              type="submit"
              variant="contained"
              className="njim-primary-btn"
              sx={{ fontSize: "13px", px: 2.5 }}
            >
              {t("returnsManagement.addItemToReturn")}
            </Button>
          </Grid>
        </Grid>
      </Card>
      </Box>
      {cart.length > 0 && (
        <Card sx={{ p: 2, mt: 2, borderRadius: "12px !important" }}>
          <Typography sx={{ fontWeight: 600, fontSize: "15px", color: NJIM.textPrimary, mb: 1.5 }}>
            {t("returnsManagement.itemsInReturn")} ({cart.length})
          </Typography>

          {cart.map((item, index) => (
            <Box
              key={index}
              sx={{
                border: "1px solid rgba(226,232,240,0.5)",
                borderRadius: "10px",
                p: 2,
                mb: 1.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "rgba(248,249,250,0.3)",
                transition: "all 0.2s ease",
                "&:hover": { background: "rgba(248,249,250,0.6)", borderColor: "rgba(226,232,240,0.8)" },
              }}
            >
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Box>
                  <Typography sx={{ fontSize: 12, color: NJIM.textMuted, fontWeight: 500 }}>
                    {t("returnsManagement.productLabel")}
                  </Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: "13px", color: NJIM.textPrimary }}>{item.name}</Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 12, color: NJIM.textMuted, fontWeight: 500 }}>
                    {t("returnsManagement.batchNumberLabel")}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: NJIM.textSecondary }}>{item.batchNumber}</Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 12, color: NJIM.textMuted, fontWeight: 500 }}>
                    {t("returnsManagement.quantityLabel")}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: NJIM.textSecondary }}>{item.quantity}</Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 12, color: NJIM.textMuted, fontWeight: 500 }}>
                    {t("returnsManagement.expiryDateLabel")}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: NJIM.textSecondary }}>{item.expiryDate}</Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 12, color: NJIM.textMuted, fontWeight: 500 }}>
                    {t("returnsManagement.reasonLabel")}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: NJIM.textSecondary }}>
                    {localizeReturnReason(item.reason, t)}
                  </Typography>
                </Box>
              </Box>

              <IconButton
                onClick={() => handleRemoveItem(item.id)}
                sx={{
                  backgroundColor: "rgba(239,68,68,0.08)",
                  color: NJIM.danger,
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "rgba(239,68,68,0.15)",
                  },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          ))}
        </Card>
      )}

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
      <Grid item xs={12} sm={12} mt={2}>
        <CustomInput
          name="notes"
          label={t("returnsManagement.additionalNotes")}
          value={form.notes}
          onChange={handleChange}
          placeholder={t("returnsManagement.provideAdditionalDetails")}
          rows={2}
        />
      </Grid>

      <Box
        sx={{
          backgroundColor: "rgba(245,158,11,0.05)",
          borderRadius: "10px",
          padding: "14px 16px",
          border: "1px solid rgba(245,158,11,0.2)",
          fontSize: "13px",
          color: NJIM.warning,
          mt: 2,
          mb: 2,
        }}
      >
        <strong>{t("returnsManagement.important")}</strong>{" "}
        {t("returnsManagement.batchNumbersCritical")}
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <Button
          type="button"
          variant="outlined"
          className="njim-secondary-btn"
          onClick={handleClose}
          sx={{ fontSize: "13px", px: 2.5 }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          variant="contained"
          className="njim-primary-btn"
          disabled={cart.length === 0}
          sx={{
            fontSize: "13px",
            px: 2.5,
          }}
        >
          {t("returnsManagement.submitReturnRequest")}
        </Button>
      </Box>
      </Box>
    </CustomModal>
  );
};

export default InitiateModal;
